// Auto-generated lightweight class for FunctionalModule
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { FunctionalModuleReadAPI, FunctionalModuleWriteAPI, FunctionalModuleReadAPISchema, FunctionalModuleWriteAPISchema } from '../types/functionalmodule';
import { ModelRegistry } from '../../registries/model-registry';
import { ActionComposedOf, Allocation, AtScope, FlowConnection, HasMembers, Performs, Satisfaction, Specializes } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { SysMLType } from '../../sysml/api/sysmltype';
import { RequirementDefinition } from '../../sysml/api/requirementdefinition';
import { PartDefinition } from '../../sysml/api/partdefinition';
import { ActionDefinition } from '../../sysml/api/actiondefinition';



export interface FunctionalModule extends Partial<FunctionalModuleReadAPI> {}
export class FunctionalModule extends NeoNodeBase<FunctionalModuleReadAPI, FunctionalModuleWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "FunctionalModule";
  static readonly readSchema = FunctionalModuleReadAPISchema;
  static readonly writeSchema = FunctionalModuleWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    general: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "out" },
    specific: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "in" },
    satisfied_requirements: { target: "RequirementDefinition", cardinality: "many", isParent: false, relClass: "Satisfaction", direction: "out" },
    allocated_to: { target: "PartDefinition", cardinality: "many", isParent: false, relClass: "Allocation", direction: "out" },
    performed_by: { target: "PartDefinition", cardinality: "many", isParent: false, relClass: "Performs", direction: "in" },
    sub_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "ActionComposedOf", direction: "out" },
    parent_action: { target: "ActionDefinition", cardinality: "one", isParent: true, relClass: "ActionComposedOf", direction: "in" },
    output_flows_to: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "FlowConnection", direction: "out" },
    input_flows_from: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "FlowConnection", direction: "in" },
  };

  protected get modelName(): string {
    return 'FunctionalModule';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return FunctionalModule.label;
  }


  constructor(data: Partial<FunctionalModuleWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: FunctionalModuleReadAPI, loadContext?: Record<string, any>): FunctionalModule {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { satisfied_requirements, allocated_to, performed_by, sub_actions, parent_action, output_flows_to, input_flows_from, security_scope, member_namespace, general, specific, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = FunctionalModuleReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, satisfied_requirements: satisfied_requirements, allocated_to: allocated_to, performed_by: performed_by, sub_actions: sub_actions, parent_action: parent_action, output_flows_to: output_flows_to, input_flows_from: input_flows_from, security_scope: security_scope, member_namespace: member_namespace, general: general, specific: specific };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return FunctionalModule.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'satisfied_requirements', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'allocated_to', 'PartDefinition', ModelRegistry);
    loadManyRelationship(processed, 'performed_by', 'PartDefinition', ModelRegistry);
    loadManyRelationship(processed, 'sub_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'parent_action', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'output_flows_to', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'input_flows_from', 'ActionDefinition', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'general', 'SysMLType', ModelRegistry);
    loadManyRelationship(processed, 'specific', 'SysMLType', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new FunctionalModule(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'general' - Python ORM style (cached)
  get general(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('general', () => {
      return new RelationshipManager(this, 'general', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'specific' - Python ORM style (cached)
  get specific(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('specific', () => {
      return new RelationshipManager(this, 'specific', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'satisfied_requirements' - Python ORM style (cached)
  get satisfied_requirements(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('satisfied_requirements', () => {
      return new RelationshipManager(this, 'satisfied_requirements', 'many', Satisfaction as typeof Relationship);
    });
  }

  // Relationship manager for 'allocated_to' - Python ORM style (cached)
  get allocated_to(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('allocated_to', () => {
      return new RelationshipManager(this, 'allocated_to', 'many', Allocation as typeof Relationship);
    });
  }

  // Relationship manager for 'performed_by' - Python ORM style (cached)
  get performed_by(): RelationshipManager<this, PartDefinition> {
    return this.getOrCreateRelationshipManager('performed_by', () => {
      return new RelationshipManager(this, 'performed_by', 'many', Performs as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_actions' - Python ORM style (cached)
  get sub_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('sub_actions', () => {
      return new RelationshipManager(this, 'sub_actions', 'many', ActionComposedOf as typeof Relationship);
    });
  }

  // Relationship manager for 'parent_action' - Python ORM style (cached)
  get parent_action(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('parent_action', () => {
      return new RelationshipManager(this, 'parent_action', 'one', ActionComposedOf as typeof Relationship);
    });
  }

  // Relationship manager for 'output_flows_to' - Python ORM style (cached)
  get output_flows_to(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('output_flows_to', () => {
      return new RelationshipManager(this, 'output_flows_to', 'many', FlowConnection as typeof Relationship);
    });
  }

  // Relationship manager for 'input_flows_from' - Python ORM style (cached)
  get input_flows_from(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('input_flows_from', () => {
      return new RelationshipManager(this, 'input_flows_from', 'many', FlowConnection as typeof Relationship);
    });
  }

}
